import { useNavigateWithFrom } from '@/lib/navWithFrom';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { EditableAvatar } from '@/components/profile/EditableAvatar';
import {
  MessageSquare,
  Shield,
  LogOut,
  ChevronRight,
  Eye,
  Package,
  Tag,
  User,
  LayoutGrid,
  CheckCircle2,
} from 'lucide-react';
import { MVP_FEATURES } from '@/lib/mvpFeatures';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useMarkSoldStore } from '@/store/markSoldStore';
import { cn } from '@/lib/utils';


import { useIdVerification } from '@/hooks/useIdVerification';
import { VerifyIdSheet } from '@/components/verification/VerifyIdSheet';
import { useMyActiveEbayListingsCount } from '@/hooks/useEbayListings';
import { useState } from 'react';
import { useChatStore } from '@/store/chatStore';

interface BottomNavMeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Row = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  iconClass?: string;
  labelClass?: string;
  trailing?: React.ReactNode;
};

function MeRow({ row, isLast }: { row: Row; isLast: boolean }) {
  const Icon = row.icon;
  return (
    <button
      type="button"
      onClick={row.onClick}
      className={cn(
        'w-full flex items-center gap-3 px-4 h-12 text-left',
        'active:bg-white/10 transition-colors',
        !isLast && 'border-b border-white/[0.06]',
      )}
    >
      <Icon className={cn('h-[18px] w-[18px] text-muted-foreground shrink-0', row.iconClass)} />
      <span className={cn('flex-1 text-[15px] font-medium truncate', row.labelClass)}>
        {row.label}
      </span>
      {row.trailing ?? (
        <ChevronRight className="h-4 w-4 text-muted-foreground/50 shrink-0" />
      )}
    </button>
  );
}

function MeGroup({ rows }: { rows: Row[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="rounded-xl bg-white/[0.04] border border-white/[0.06] overflow-hidden">
      {rows.map((row, i) => (
        <MeRow key={row.label} row={row} isLast={i === rows.length - 1} />
      ))}
    </div>
  );
}

export function BottomNavMeSheet({ open, onOpenChange }: BottomNavMeSheetProps) {
  const navigate = useNavigateWithFrom();
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const { isAdmin } = useIsAdmin();
  const { data: idVer } = useIdVerification();
  const { data: activeEbayCount = 0 } = useMyActiveEbayListingsCount();
  const [verifyOpen, setVerifyOpen] = useState(false);
  const openChat = useChatStore((s) => s.open);
  const openMarkSold = useMarkSoldStore((s) => s.openMarkSold);

  // Temporarily hidden menu rows. Flip to true to re-enable.
  const SHOW_VERIFY_ID_ROW = false;
  const SHOW_CONTACT_ROW = false;

  const go = (path: string) => {
    navigate(path);
    onOpenChange(false);
  };

  const openMessages = () => {
    onOpenChange(false);
    setTimeout(() => openChat(), 80);
  };

  const idVerifyRow: Row | null = (() => {
    if (!user || !SHOW_VERIFY_ID_ROW) return null;
    if (idVer?.status === 'approved') return null;
    const isPending = idVer?.status === 'pending';
    const isRejected = idVer?.status === 'rejected';
    return {
      icon: Shield,
      label: isPending ? 'ID under review' : isRejected ? 'ID rejected — try again' : 'Verify your ID',
      iconClass: isPending ? 'text-muted-foreground' : 'text-active-gold',
      labelClass: isPending ? '' : 'text-active-gold',
      trailing: !isPending ? (
        <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-active-gold/15 text-active-gold border border-active-gold/30">
          Recommended
        </span>
      ) : undefined,
      onClick: () => {
        if (isPending) { onOpenChange(false); return; }
        onOpenChange(false);
        setTimeout(() => setVerifyOpen(true), 50);
      },
    } as Row;
  })();

  const primaryRows: Row[] = [
    ...(idVerifyRow ? [idVerifyRow] : []),
    { icon: Package, label: 'Inventory', onClick: () => go('/inventory') },
    { icon: MessageSquare, label: 'Messages', onClick: openMessages },
    ...(profile?.username
      ? [{ icon: Eye, label: 'Preview Storefront', onClick: () => go('/preview') } as Row]
      : []),
    ...(activeEbayCount > 0
      ? [{ icon: Tag, label: 'My eBay Listings', onClick: () => go('/ebay/listings') } as Row]
      : []),
    {
      icon: CheckCircle2,
      label: 'Mark Cards as Sold',
      onClick: () => { onOpenChange(false); setTimeout(() => openMarkSold(), 80); },
    },
    { icon: User, label: 'Profile / Settings', onClick: () => go('/settings') },
    ...(MVP_FEATURES.menus
      ? [{ icon: LayoutGrid, label: 'Menus & Shortcuts', onClick: () => go('/menus') } as Row]
      : []),
    ...(SHOW_CONTACT_ROW
      ? [{ icon: MessageSquare, label: 'Contact', onClick: () => go('/contact') } as Row]
      : []),
    ...(isAdmin ? [{ icon: Shield, label: 'Admin', onClick: () => go('/admin') } as Row] : []),
  ];

  const collectingRows: Row[] = [];

  const secondaryRows: Row[] = [];


  const signOutRows: Row[] = [
    {
      icon: LogOut,
      label: 'Sign Out',
      iconClass: 'text-destructive',
      labelClass: 'text-destructive',
      trailing: <span />,
      onClick: () => {
        signOut();
        onOpenChange(false);
      },
    },
  ];

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
        <DrawerContent
          className={cn(
            'border-white/[0.08] bg-background',
            'px-3 pb-[calc(env(safe-area-inset-bottom)+1rem)]',
          )}
        >
          <DrawerHeader className="sr-only">
            <DrawerTitle>Account menu</DrawerTitle>
          </DrawerHeader>

          {user && (
            <button
              type="button"
              onClick={() => go('/settings')}
              className={cn(
                'w-full flex items-center gap-3 mt-2 mb-3 p-3 rounded-xl text-left',
                'bg-white/[0.04] border border-white/[0.06] active:bg-white/[0.08] transition-colors',
              )}
            >
              <EditableAvatar
                avatarUrl={profile?.avatar_url}
                username={profile?.username}
                email={user.email}
                size="lg"
                editable={false}
              />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[15px] truncate">
                  {profile?.username || user.email?.split('@')[0] || 'User'}
                </p>
                <p className="text-xs text-muted-foreground/80 truncate">{user.email}</p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground/50 shrink-0" />
            </button>
          )}

          <div className="space-y-3">
            <MeGroup rows={primaryRows} />
            <MeGroup rows={collectingRows} />
            <MeGroup rows={secondaryRows} />
            <MeGroup rows={signOutRows} />
          </div>
        </DrawerContent>
      </Drawer>
      <VerifyIdSheet open={verifyOpen} onOpenChange={setVerifyOpen} />
    </>
  );
}
